import { useState, useEffect } from 'react';

interface DashboardMocksViewProps {
  userId?: string; // Optional, not currently used but kept for consistency with other dashboard views
}

interface MockEndpoint {
  id: string;
  endpoint_name: string;
  user_hash: string;
  data_source: 'custom' | 'template';
  template_type?: string;
  custom_json?: any;
  description?: string;
  http_methods: string[];
  response_delay_ms: number;
  status_code: number;
  total_requests: number;
  last_accessed_at?: string;
  created_at: string;
  is_active: boolean;
  url: string;
}

interface Template {
  name: string;
  description: string;
  category: string;
}

export default function DashboardMocksView({ userId: _userId }: DashboardMocksViewProps) {
  const [mockEndpoints, setMockEndpoints] = useState<MockEndpoint[]>([]);
  const [templates, setTemplates] = useState<Record<string, Template>>({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedEndpoint, setSelectedEndpoint] = useState<MockEndpoint | null>(null);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);

  useEffect(() => {
    loadMockEndpoints();
    loadTemplates();
  }, []);

  const loadMockEndpoints = async () => {
    try {
      const response = await fetch('/api/dashboard/mocks/list');
      const data = await response.json();

      if (data.success) {
        setMockEndpoints(data.mockEndpoints);
      }
    } catch (error) {
      console.error('Failed to load mock endpoints:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await fetch('/api/dashboard/mocks/templates');
      const data = await response.json();

      if (data.success) {
        setTemplates(data.templates);
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const handleCreateNew = () => {
    setSelectedEndpoint(null);
    setModalMode('create');
    setShowModal(true);
  };

  const handleEdit = (endpoint: MockEndpoint) => {
    setSelectedEndpoint(endpoint);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleDelete = async (endpoint: MockEndpoint) => {
    if (!confirm(`Are you sure you want to delete "${endpoint.endpoint_name}"?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/dashboard/mocks/${endpoint.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await loadMockEndpoints();
      } else {
        alert('Failed to delete mock endpoint');
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete mock endpoint');
    }
  };

  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-coral-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-coral-600 to-teal-600 bg-clip-text text-transparent">
              Mock API Generator
            </h2>
            <p className="text-gray-600 mt-1">
              Create custom mock APIs for development and testing
            </p>
          </div>
          <button
            onClick={handleCreateNew}
            className="px-4 py-2 bg-gradient-to-r from-coral-500 to-teal-500 text-white rounded-lg hover:shadow-lg transition-all font-medium"
          >
            + Create New
          </button>
        </div>
      </div>

      {/* Mock Endpoints List */}
      {mockEndpoints.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
          <svg
            className="w-16 h-16 mx-auto text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
            />
          </svg>
          <h3 className="text-lg font-semibold text-gray-900 mt-4">No mock APIs yet</h3>
          <p className="text-gray-600 mt-2">Create your first mock API to get started</p>
          <button
            onClick={handleCreateNew}
            className="mt-6 px-6 py-3 bg-gradient-to-r from-coral-500 to-teal-500 text-white rounded-lg hover:shadow-lg transition-all font-medium"
          >
            Create Mock API
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            {mockEndpoints.length} / 10 mock endpoints
          </div>

          {mockEndpoints.map((endpoint) => (
            <div
              key={endpoint.id}
              className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${endpoint.is_active ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                    <h3 className="text-xl font-semibold text-gray-900">{endpoint.endpoint_name}</h3>
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-md font-medium">
                      {endpoint.data_source === 'template' ? 'Template' : 'Custom'}
                    </span>
                  </div>

                  {endpoint.description && (
                    <p className="text-gray-600 mt-2">{endpoint.description}</p>
                  )}

                  {endpoint.data_source === 'template' && endpoint.template_type && templates[endpoint.template_type] && (
                    <p className="text-sm text-gray-500 mt-1">
                      Template: {templates[endpoint.template_type].name}
                    </p>
                  )}

                  <div className="mt-4 flex items-center gap-2">
                    <input
                      type="text"
                      value={endpoint.url}
                      readOnly
                      className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono text-gray-700"
                    />
                    <button
                      onClick={() => handleCopyUrl(endpoint.url)}
                      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm font-medium"
                    >
                      {copiedUrl === endpoint.url ? '✓ Copied' : 'Copy'}
                    </button>
                  </div>

                  {/* CURL Examples */}
                  <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="text-xs font-semibold text-gray-700 mb-2">Usage Examples:</div>
                    <div className="space-y-2">
                      <div>
                        <div className="text-xs text-gray-600 mb-1">Basic request (10 req/hour):</div>
                        <div className="bg-gray-900 text-gray-100 px-3 py-2 rounded font-mono text-xs overflow-x-auto">
                          curl {endpoint.url}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-600 mb-1">With API key (500 req/hour, 10K/month):</div>
                        <div className="bg-gray-900 text-gray-100 px-3 py-2 rounded font-mono text-xs overflow-x-auto">
                          curl -H "X-API-Key: your-api-key" {endpoint.url}
                        </div>
                      </div>
                      {endpoint.http_methods.includes('POST') && (
                        <div>
                          <div className="text-xs text-gray-600 mb-1">POST request with data:</div>
                          <div className="bg-gray-900 text-gray-100 px-3 py-2 rounded font-mono text-xs overflow-x-auto">
                            curl -X POST -H "Content-Type: application/json" -d '{`{}`}' {endpoint.url}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 flex items-center gap-6 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                      <span>{endpoint.total_requests.toLocaleString()} requests</span>
                    </div>
                    {endpoint.last_accessed_at && (
                      <div>Last used: {formatDate(endpoint.last_accessed_at)}</div>
                    )}
                    <div className="flex items-center gap-1">
                      <span>Methods:</span>
                      <span className="font-mono">{endpoint.http_methods.join(', ')}</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => handleEdit(endpoint)}
                    className="p-2 text-gray-600 hover:text-coral-600 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDelete(endpoint)}
                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <MockAPIModal
          mode={modalMode}
          endpoint={selectedEndpoint}
          templates={templates}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            loadMockEndpoints();
          }}
        />
      )}
    </div>
  );
}

// Mock API Create/Edit Modal Component
interface MockAPIModalProps {
  mode: 'create' | 'edit';
  endpoint: MockEndpoint | null;
  templates: Record<string, Template>;
  onClose: () => void;
  onSuccess: () => void;
}

function MockAPIModal({ mode, endpoint, templates, onClose, onSuccess }: MockAPIModalProps) {
  const [formData, setFormData] = useState({
    endpointName: endpoint?.endpoint_name || '',
    dataSource: endpoint?.data_source || 'template' as 'template' | 'custom',
    templateType: endpoint?.template_type || Object.keys(templates)[0] || '',
    customJson: endpoint?.custom_json ? JSON.stringify(endpoint.custom_json, null, 2) : '',
    description: endpoint?.description || '',
    httpMethods: endpoint?.http_methods || ['GET'],
    responseDelay: endpoint?.response_delay_ms || 0,
    statusCode: endpoint?.status_code || 200
  });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      // Validate
      if (!formData.endpointName) {
        setError('Endpoint name is required');
        setSubmitting(false);
        return;
      }

      if (!/^[a-z0-9-]+$/.test(formData.endpointName)) {
        setError('Endpoint name must contain only lowercase letters, numbers, and hyphens');
        setSubmitting(false);
        return;
      }

      let customJsonParsed = null;
      if (formData.dataSource === 'custom') {
        try {
          customJsonParsed = JSON.parse(formData.customJson);
        } catch {
          setError('Invalid JSON format');
          setSubmitting(false);
          return;
        }
      }

      const payload = {
        endpointName: formData.endpointName,
        dataSource: formData.dataSource,
        templateType: formData.dataSource === 'template' ? formData.templateType : undefined,
        customJson: formData.dataSource === 'custom' ? customJsonParsed : undefined,
        description: formData.description,
        httpMethods: formData.httpMethods,
        responseDelay: formData.responseDelay,
        statusCode: formData.statusCode
      };

      const url = mode === 'create'
        ? '/api/dashboard/mocks/create'
        : `/api/dashboard/mocks/${endpoint?.id}`;

      const method = mode === 'create' ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to save mock endpoint');
        setSubmitting(false);
        return;
      }

      onSuccess();
    } catch (error) {
      console.error('Submit error:', error);
      setError('An error occurred');
      setSubmitting(false);
    }
  };

  const toggleHttpMethod = (method: string) => {
    if (formData.httpMethods.includes(method)) {
      setFormData({
        ...formData,
        httpMethods: formData.httpMethods.filter(m => m !== method)
      });
    } else {
      setFormData({
        ...formData,
        httpMethods: [...formData.httpMethods, method]
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            {mode === 'create' ? 'Create Mock API' : 'Edit Mock API'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Endpoint Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Endpoint Name *
            </label>
            <input
              type="text"
              value={formData.endpointName}
              onChange={(e) => setFormData({ ...formData, endpointName: e.target.value.toLowerCase() })}
              placeholder="my-api-endpoint"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
              required
            />
            <p className="text-xs text-gray-500 mt-1">Only lowercase, numbers, and hyphens</p>
          </div>

          {/* Data Source */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Data Source *
            </label>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, dataSource: 'template' })}
                className={`flex-1 px-4 py-3 border-2 rounded-lg transition-all ${
                  formData.dataSource === 'template'
                    ? 'border-coral-500 bg-coral-50 text-coral-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-medium">Use Template</div>
                <div className="text-xs text-gray-600 mt-1">Choose from pre-built templates</div>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, dataSource: 'custom' })}
                className={`flex-1 px-4 py-3 border-2 rounded-lg transition-all ${
                  formData.dataSource === 'custom'
                    ? 'border-coral-500 bg-coral-50 text-coral-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-medium">Custom JSON</div>
                <div className="text-xs text-gray-600 mt-1">Provide your own data</div>
              </button>
            </div>
          </div>

          {/* Template Selection */}
          {formData.dataSource === 'template' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Template
              </label>
              <select
                value={formData.templateType}
                onChange={(e) => setFormData({ ...formData, templateType: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
              >
                {Object.entries(templates).map(([key, template]) => (
                  <option key={key} value={key}>
                    {template.name} - {template.description}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Custom JSON */}
          {formData.dataSource === 'custom' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Custom JSON *
              </label>
              <textarea
                value={formData.customJson}
                onChange={(e) => setFormData({ ...formData, customJson: e.target.value })}
                placeholder='{"message": "Hello World", "data": []}'
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500 font-mono text-sm"
                rows={10}
                required
              />
              <p className="text-xs text-gray-500 mt-1">Max 100KB</p>
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description (Optional)
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of this mock API..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
            />
          </div>

          {/* HTTP Methods */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              HTTP Methods
            </label>
            <div className="flex flex-wrap gap-2">
              {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((method) => (
                <button
                  key={method}
                  type="button"
                  onClick={() => toggleHttpMethod(method)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    formData.httpMethods.includes(method)
                      ? 'bg-coral-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {method}
                </button>
              ))}
            </div>
          </div>

          {/* Advanced Options */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Response Delay (ms)
              </label>
              <input
                type="number"
                value={formData.responseDelay}
                onChange={(e) => setFormData({ ...formData, responseDelay: parseInt(e.target.value) || 0 })}
                min="0"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status Code
              </label>
              <select
                value={formData.statusCode}
                onChange={(e) => setFormData({ ...formData, statusCode: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
              >
                <option value="200">200 - OK</option>
                <option value="201">201 - Created</option>
                <option value="204">204 - No Content</option>
                <option value="400">400 - Bad Request</option>
                <option value="401">401 - Unauthorized</option>
                <option value="404">404 - Not Found</option>
                <option value="500">500 - Server Error</option>
              </select>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-gradient-to-r from-coral-500 to-teal-500 text-white rounded-lg hover:shadow-lg transition-all font-medium disabled:opacity-50"
              disabled={submitting}
            >
              {submitting ? 'Saving...' : mode === 'create' ? 'Create Mock API' : 'Update Mock API'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
